import { Component, inject, Input, Pipe, PipeTransform, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzImageModule } from 'ng-zorro-antd/image';
import { UserAvatarComponent, SafePipe, AudioPlayerComponent, FlIconComponent } from '@common';
import { SharedService } from '@shared/service/shared.service';
import { MessageSummaryComponent } from '../message-summary/message-summary.component';
import { EmjioIconNamePipe } from '../emjio-icon-name.pipe';
import { Message, MessageTag } from '../../message.api.service';
import { MessageTypeNamePipe } from '../../message.pipe';

export type MediaKind = 'image' | 'video' | 'audio' | 'file';

/**
 * 消息附件类型 Pipe：按 URL 后缀判断附件类型
 *
 * 图片 → image（缩略图预览）；视频 → video（封面+播放图标）；音频 → audio（内联播放器）；其他 → file
 */
@Pipe({
  name: 'mediaType',
  standalone: true,
})
export class MediaTypePipe implements PipeTransform {
  private static readonly IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif'];
  private static readonly VIDEO_EXT = ['mp4', 'webm', 'mov', 'ogg', 'm4v', 'avi', 'ts'];
  private static readonly AUDIO_EXT = ['mp3', 'wav', 'amr', 'aac', 'm4a'];

  transform(url: string): MediaKind {
    const suffix = this.getSuffix(url);
    if (!suffix) return 'file';
    if (MediaTypePipe.IMAGE_EXT.includes(suffix)) return 'image';
    if (MediaTypePipe.VIDEO_EXT.includes(suffix)) return 'video';
    if (MediaTypePipe.AUDIO_EXT.includes(suffix)) return 'audio';
    return 'file';
  }

  private getSuffix(url: string): string {
    if (!url) return '';
    const clean = url.split('?')[0];
    const lastDot = clean.lastIndexOf('.');
    if (lastDot < 0) return '';
    return clean.slice(lastDot + 1).toLowerCase();
  }
}

/**
 * 消息内容是否富文本 Pipe：嗅探 content 是否含 HTML 标签
 *
 * 旧数据 content 为编辑器导出的 HTML（如 `<p>...<img/>...</p>`），新数据为纯文本。
 * 历史兼容：含标签 → 富文本渲染；纯文本 → 按行文原样渲染。
 */
@Pipe({
  name: 'messageContent',
  standalone: true,
})
export class MessageContentPipe implements PipeTransform {
  /** 匹配 `<tag>`/`</tag>`/自闭合 `<tag/>` 的 HTML 标签，避开文本中的 `<`/`>` 比较符 */
  private static readonly HTML_TAG = /<\/?[a-zA-Z][^>]*>/;

  transform(content: string | undefined): boolean {
    if (!content) return false;
    return MessageContentPipe.HTML_TAG.test(content);
  }
}

/**
 * 标签文字显隐 Pipe：消息无正文时标签展示「emoji + 标签名/人名」，有正文时只留 emoji
 */
@Pipe({
  name: 'tagTextVisible',
  standalone: true,
})
export class TagTextVisiblePipe implements PipeTransform {
  transform(content: string | undefined): boolean {
    return !content || content.trim().length === 0;
  }
}

export interface MessageTagGroup {
  tag: string; // tag 值（如 '听懂了'、'感谢'）
  users: MessageTag[]; // 该 tag 下的人
  count: number; // 人数
  namesText: string; // 展示的人名（逗号分隔，如 `张三，李四`）
  collapseText: string; // 超出折叠时显示的 `...等N人`；未超出为空串
}

/**
 * 消息标签聚合 Pipe
 *
 * 前端按 tag 值分组：同 tag >= 2 条 → 聚合为人名单胶囊（前 2 人全列，超过折叠为 `..等N人`）；
 * 只同 tag = 1 条 → 单按钮，模板按 count 区分渲染。
 */
@Pipe({
  name: 'messageTagView',
  standalone: true,
})
export class MessageTagViewPipe implements PipeTransform {
  /** 折叠临界：全展开展示的姓名前缀人数 */
  private static readonly COLLAPSE_AFTER = 2;

  transform(tags: MessageTag[] | undefined): MessageTagGroup[] {
    if (!tags || tags.length === 0) return [];

    const map = new Map<string, MessageTag[]>();
    for (const t of tags) {
      const list = map.get(t.tag) ?? [];
      list.push(t);
      map.set(t.tag, list);
    }

    const groups: MessageTagGroup[] = [];
    for (const [tag, users] of map) {
      const count = users.length;
      const isAggregated = count >= 2;
      const previewCount = isAggregated ? Math.min(count, MessageTagViewPipe.COLLAPSE_AFTER) : count;
      const collapseText = count > previewCount ? `...等${count}人` : '';
      const names = users.slice(0, previewCount).map(u => u.firstName);
      groups.push({
        tag,
        users,
        count,
        namesText: names.join('，'),
        collapseText,
      });
    }

    return groups;
  }
}

/**
 * 已读进度环中心饼图 Pipe：返回 CSS conic-gradient，绘制已读占比的实心扇形
 *
 * 占比 = hasReadCount / totalCount；只负责中心饼图（绿扇 + 透明漏底色），外圈空心圆由模板 border 承担。
 * 全未读 / 全部已读返回 none（分别交给空心灰边、对勾 SVG 呈现）。
 */
@Pipe({
  name: 'readRing',
  standalone: true,
})
export class ReadRingPipe implements PipeTransform {
  transform(hasReadCount: number, totalCount: number): string {
    if (totalCount <= 0) return 'none';
    const ratio = Math.min(1, hasReadCount / totalCount);
    if (ratio <= 0 || ratio >= 1) return 'none';
    const deg = Math.round(ratio * 360);
    return `conic-gradient(#10A993 0deg ${deg}deg, transparent ${deg}deg 360deg)`;
  }
}

/**
 * 单条消息（纯展示组件）
 *
 * 领域模型见 message/README.md：
 * - isSender：message.userId !== 当前登录用户 → 本人消息居右（浅蓝气泡、不显示姓名、显示已读进度环），对方居左（浅灰气泡、显示姓名）
 * - 引用：父级传完整 parentMessage，读 firstName/content 渲染，不做异步复查
 * - 标签：前端按 tag 值分组，同 tag >= 2 聚合为人名单胶囊，=1 单按钮
 * - 已读指示：CSS conic-gradient 进度环，占比 hasReadCount/totalCount，100% 变对勾，仅本人消息
 * - 插槽：仅姓名栏 nameSlot
 * - 纯展示：无 @Output、无点击副作用
 */
@Component({
  selector: 'fl-message',
  templateUrl: './message.component.html',
  styleUrls: ['./message.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NzImageModule,
    UserAvatarComponent,
    SafePipe,
    AudioPlayerComponent,
    MessageTagViewPipe,
    MediaTypePipe,
    ReadRingPipe,
    MessageContentPipe,
    TagTextVisiblePipe,
    MessageTypeNamePipe,
    MessageSummaryComponent,
    FlIconComponent,
    EmjioIconNamePipe,
  ],
})
export class MessageComponent {
  /** 当前登录用户 */
  private sharedSrv = inject(SharedService);

  /** 单条消息体 */
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() message: Message;

  /** 引用父消息（完整消息体，由父级传入；不传入则不渲染引用块） */
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() parentMessage?: Message;

  /** 是否正在回复该条消息（右侧展示发送时间，帮助确认回复上下文） */
  @Input() replying = false;

  /** 是否本人发送（左右分栏依据） */
  get isSender() {
    return this.message?.userId === this.sharedSrv.user?.user_id;
  }

  /** 点击消息气泡回复（点击被回复人的消息→唤起父级回复器） */
  readonly avatarClick = output<Message>();

  /** 点击消息气泡：本人消息不触发；落在交互子元素（链接/图片/媒体/引用）或拖拽选中文本时不触发，避免误触 */
  onBubbleClick(event: Event) {
    if (this.isSender) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;
    if (target.closest('a, img, video, audio, lib-audio-player, .message-quote')) return;
    this.avatarClick.emit(this.message);
  }

  /** 气泡外右下角的「回复」按钮：与点击气泡同义，显式入口便于老师发现 */
  onReplyClick() {
    this.avatarClick.emit(this.message);
  }

  /** 姓名等级角标等由调用方通过 nameSlot 注入，本组件不关心 */
}
